import { useCallback, useMemo, useState } from 'react';

import { resolveGutXAxisSystems } from '#components/GutXAxisBody/systems';
import type { GutXAxisOrgan, OrganLookupCode } from '@repo/types';

type UseGutXAxisImagesArgs = {
  organs: GutXAxisOrgan[];
  /** `kit.age_at_sampling`, in days. */
  age: number;
  pregnant: boolean;
  sex: string;
  drawnCodes?: ReadonlySet<OrganLookupCode>;
  waitForImages?: boolean;
};

/**
 * Reports when a body's drawings have all loaded, so the organs appear together
 * rather than one at a time.
 *
 * The count comes from the same parts that get rendered: an organ can own two
 * drawings or none, so any separately derived total would deadlock the paediatric
 * bodies, where fewer organs render than the kit has.
 */
export function useGutXAxisImages({
  organs,
  age,
  pregnant,
  sex,
  drawnCodes,
  waitForImages = true,
}: UseGutXAxisImagesArgs) {
  const resolved = useMemo(
    () => resolveGutXAxisSystems({ organs, age, pregnant, sex, drawnCodes }),
    [organs, age, pregnant, sex, drawnCodes],
  );

  const partCount = resolved.systems.reduce(
    (total, system) => total + system.parts.length,
    0,
  );

  const [loaded, setLoaded] = useState<ReadonlySet<string>>(new Set());

  const mark = useCallback(
    (part: string) => {
      if (!waitForImages) return;
      setLoaded(prev => (prev.has(part) ? prev : new Set(prev).add(part)));
    },
    [waitForImages],
  );

  const imgProps = useCallback(
    (part: string) => ({
      // A cached drawing can finish before React attaches onLoad, so count it here.
      ref: (img: HTMLImageElement | null) => {
        if (img?.complete) mark(part);
      },
      onLoad: () => mark(part),
      // A drawing that fails still counts, or one bad asset holds the body forever.
      onError: () => mark(part),
    }),
    [mark],
  );

  return {
    ...resolved,
    imgProps,
    ready: !waitForImages || loaded.size >= partCount + 1,
  };
}
